import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Icon,
  IconButton,
  Stack,
  Tag,
  Text,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaTimes } from "react-icons/fa";
import {
  EmailInviteLanguage,
  InvitedPersonDto,
  PanelMemberDto,
  SourcingEventDto,
  UnregisteredPanelMemberDto,
  SourcingEventPatchDto,
  useGetMiniCompetitionPanelQuery,
  useGetSourcingEventQuery,
  useRemoveEmailInviteFromPrivateSourcingEventMutation,
} from "../../../../../../autogen/bff-api";
import { EntityViewSkeleton } from "../../../../../../common/EntityViewSkeleton";
import { useEditor } from "../../../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../../../common/errors/useApiError";
import { StepNavigation } from "../../../../../../common/StepNavigation";
import { activePanel, reachableUnregistered } from "../../../../../framework-agreement/callOffAvailability";
import { InviteOrganizationEntryModal } from "../../../../../sourcing-event/InviteOrganizationEntryModal";

import {
  InviteOrganizationModal,
  type OrganizationInviteRecipients,
} from "../private/InviteOrganizationModal";

interface EmailInvite {
  email: string;
  language: EmailInviteLanguage;
}

const nameOf = (person: InvitedPersonDto): string =>
  [person.firstName, person.lastName].filter(Boolean).join(" ").trim() || (person.email ?? "");

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

// FOA § 26-3: only suppliers already on the panel may take part.
export const MiniCompetitionInvitationsStep = ({ eventId, previousStep, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const [choosingFor, setChoosingFor] = useState<PanelMemberDto | null>(null);
  const [emailing, setEmailing] = useState<UnregisteredPanelMemberDto | null>(null);
  const { state, updateField, entityStatus } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const context = state.eventContext.type === "MiniCompetition" ? state.eventContext : null;
  const {
    data: framework,
    error,
    isLoading,
    refetch,
  } = useGetMiniCompetitionPanelQuery({ eventId }, { skip: !context });
  const { data: serverEvent } = useGetSourcingEventQuery({ eventId });
  const [removeEmailInvite, { isLoading: isRemovingEmailInvite }] =
    useRemoveEmailInviteFromPrivateSourcingEventMutation();
  const [justTicked, setJustTicked] = useState<PanelMemberDto | null>(null);
  const [refused, setRefused] = useState<PanelMemberDto | null>(null);
  const sawSaveStart = useRef(false);
  const invitedKey = (context?.organizationInvites ?? []).map((invite) => invite.organizationId).join(",");

  useEffect(() => {
    if (!justTicked) return;
    if (entityStatus !== "saved") {
      sawSaveStart.current = true;
      return;
    }
    if (!sawSaveStart.current) return;
    if (!invitedKey.split(",").includes(justTicked.organizationId)) {
      setRefused(justTicked);
      void refetch();
    }
    sawSaveStart.current = false;
    setJustTicked(null);
  }, [entityStatus, justTicked, invitedKey, refetch]);

  if (!context) return <Text pt="10">{t("Invitations apply to invited procurements only.")}</Text>;
  if (error) return displayer.show(error);
  if (isLoading || !framework) return <EntityViewSkeleton />;

  const emailInvites =
    serverEvent?.eventContext.type === "MiniCompetition" ? serverEvent.eventContext.emailInvites ?? [] : [];
  const invited = new Set(context.organizationInvites.map((invite) => invite.organizationId));
  const activeMembers = activePanel(framework);
  const unregisteredMembers = reachableUnregistered(framework);
  const anyContractEnded = [...(framework.panel ?? []), ...(framework.unregisteredSuppliers ?? [])].some(
    (member) => member.contractEnded,
  );
  const refusedContractEnded =
    !!refused &&
    !!(framework.panel ?? []).find((member) => member.organizationId === refused.organizationId)?.contractEnded;

  const save = (invites: { organizationId: string; persons: { id: string }[]; emailInvites: EmailInvite[] }[]) =>
    updateField({
      miniCompetitionContext: {
        organizationInvites: invites.map((invite) => ({
          organizationId: invite.organizationId,
          persons: invite.persons.map((person) => ({ id: person.id })),
          emailInvites: invite.emailInvites,
        })),
      },
    });

  const currentInvites = () =>
    context.organizationInvites.map((invite) => ({
      organizationId: invite.organizationId,
      persons: (invite.persons ?? []).map((person) => ({ id: person.id })),
      emailInvites: (invite.emailInvites ?? []).map((e) => ({ email: e.email, language: e.language })),
    }));

  const remove = async (inviteId: string) => {
    try {
      await removeEmailInvite({ eventId, inviteId }).unwrap();
    } catch (e) {
      displayer.trigger(e);
    }
  };

  const toggle = (member: PanelMemberDto, next: boolean) => {
    const organizationId = member.organizationId;
    setRefused(null);
    sawSaveStart.current = false;
    setJustTicked(next ? member : null);
    save(
      next
        ? [...currentInvites(), { organizationId, persons: [], emailInvites: [] }]
        : currentInvites().filter((invite) => invite.organizationId !== organizationId),
    );
  };

  const setRecipients = (organizationId: string, recipients: OrganizationInviteRecipients) =>
    save(
      currentInvites().map((invite) =>
        invite.organizationId === organizationId
          ? {
              organizationId,
              persons: recipients.personIds.map((id) => ({ id })),
              emailInvites: recipients.emailInvites,
            }
          : invite,
      ),
    );

  return (
    <Box pb="8" pt="10">
      <Heading as="h2" size="md">
        {t("Which suppliers on the panel take part")}
      </Heading>
      <Text fontSize="sm" color="smMuted" mt="1">
        {anyContractEnded
          ? t(
              "Suppliers that are suspended, or whose contract under the framework agreement has ended, cannot be invited to a mini-competition.",
            )
          : t("Suspended suppliers cannot be invited to a mini-competition.")}
      </Text>
      {refused && (
        <Alert status="warning" rounded="md" mt="4" fontSize="sm">
          <AlertIcon />
          {refusedContractEnded
            ? t("{{supplier}}'s contract under the framework agreement has ended, so it can't be invited.", {
                supplier: refused.name,
              })
            : t("{{supplier}} is suspended from the framework agreement and can't be invited.", {
                supplier: refused.name,
              })}
        </Alert>
      )}
      <Stack mt="5" spacing="3">
        {activeMembers.length === 0 && unregisteredMembers.length === 0 && (
          <Text color="smMuted">
            {anyContractEnded
              ? t("No supplier on this framework agreement can be invited: each is suspended or its contract has ended.")
              : t("No suppliers on this framework agreement are active.")}
          </Text>
        )}
        {activeMembers.map((member) => {
          const invite = context.organizationInvites.find((i) => i.organizationId === member.organizationId);
          const named = invite?.persons ?? [];
          const emailed = invite?.emailInvites ?? [];
          return (
            <Box
              key={member.organizationId}
              borderWidth="1px"
              borderColor="smBorder"
              rounded="md"
              px="4"
              py="3"
            >
              <Flex align="center" justify="space-between" gap="3">
                <Checkbox
                  isChecked={invited.has(member.organizationId)}
                  onChange={(e) => toggle(member, e.target.checked)}
                >
                  {member.name}
                </Checkbox>
                {member.ranking != null && <Tag size="sm">{t("Ranked {{rank}}", { rank: member.ranking })}</Tag>}
              </Flex>
              {invite && (
                <Flex align="baseline" justify="space-between" gap="3" mt="2" pl="6" flexWrap="wrap">
                  <Text fontSize="sm" color="smMuted">
                    {named.length === 0 && emailed.length === 0
                      ? t("Goes to the administrators of the supplier organisation.")
                      : t("Goes to: {{recipients}}", {
                          recipients: [...named.map(nameOf), ...emailed.map((e) => e.email)].join(", "),
                        })}
                  </Text>
                  <Button variant="link" size="sm" colorScheme="teal" onClick={() => setChoosingFor(member)}>
                    {t("Change")}
                  </Button>
                </Flex>
              )}
            </Box>
          );
        })}
        {unregisteredMembers.map((member) => {
          const emailed = emailInvites.filter(
            (invite) => invite.organizationEntry?.id === member.organizationEntryId,
          );
          return (
            <Box
              key={member.organizationEntryId}
              borderWidth="1px"
              borderColor="smBorder"
              rounded="md"
              px="4"
              py="3"
            >
              <Flex align="center" justify="space-between" gap="3">
                <Text fontWeight="medium">{member.name}</Text>
                <Tag size="sm">{t("Not registered in SourceMagnet")}</Tag>
              </Flex>
              {emailed.map((invite) => (
                <Flex key={invite.id} align="center" gap="1" mt="2">
                  <Text fontSize="sm" color="smMuted" overflowWrap="anywhere">
                    {t("Invited by e-mail: {{emails}}", { emails: invite.email })}
                  </Text>
                  <IconButton
                    aria-label={t("Remove {{email}}", { email: invite.email })}
                    icon={<Icon as={FaTimes} />}
                    size="xs"
                    variant="ghost"
                    isDisabled={isRemovingEmailInvite}
                    onClick={() => remove(invite.id)}
                  />
                </Flex>
              ))}
              <Flex align="baseline" justify="space-between" gap="3" mt="2" flexWrap="wrap">
                <Text fontSize="sm" color="smMuted">
                  {emailed.length === 0 && t("Not invited yet. The supplier can only be invited by e-mail.")}
                </Text>
                <Button variant="link" size="sm" colorScheme="teal" onClick={() => setEmailing(member)}>
                  {t("Invite by e-mail")}
                </Button>
              </Flex>
            </Box>
          );
        })}
      </Stack>
      <InviteOrganizationEntryModal
        eventId={eventId}
        minimalOrgEntry={emailing ? { id: emailing.organizationEntryId, name: emailing.name } : undefined}
        alreadyInvited={emailInvites}
        onClose={() => setEmailing(null)}
      />
      <InviteOrganizationModal
        eventId={eventId}
        minimalOrg={choosingFor ? { id: choosingFor.organizationId, name: choosingFor.name } : undefined}
        onClose={() => setChoosingFor(null)}
        onCreate={(recipients) => {
          if (choosingFor) setRecipients(choosingFor.organizationId, recipients);
          setChoosingFor(null);
        }}
      />
      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
